import { useState } from "react";
import { Seo } from "../components/Seo";
import { downloadAircraftBrief } from "../lib/aircraftBrief";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Users, Gauge, Luggage } from "lucide-react";
import { PA_BASE, IMAGES, img } from "../config";
import { KineticHero } from "../components/KineticHero";
import { Reveal, Overline, EASE } from "../components/motion";
import { AIRCRAFT, AIRCRAFT_CATEGORIES } from "../data/aircraft";

export default function AircraftPage() {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? AIRCRAFT : AIRCRAFT.filter((a) => a.category === filter);

  return (
    <>
      <Seo
        title="Aircraft — Light, Midsize, Super Midsize & Heavy Jets, Turboprops, Helicopters | JS Private Aviation"
        description="Aircraft examples available through our global operator network — from Phenom 300 and Citation XLS+ to Global 6000, Gulfstream G550 and Falcon 7X."
      />
      <KineticHero
        testId="aircraft-hero"
        fullHeight={false}
        overline="Aircraft"
        lines={["The right aircraft,", "not our aircraft."]}
        sub="We hold no fleet — so nothing we suggest is stock we need to move. Aircraft examples below are available through our global operator network."
        image={img(IMAGES.jetFlying, 2000)}
      />

      <section className="bg-[#F9F9F6] py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-2" data-testid="aircraft-filters">
              {["All", ...AIRCRAFT_CATEGORIES].map((cat) => (
                <button
                  key={cat}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setFilter(cat)}
                  className={`border px-5 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                    filter === cat
                      ? "border-black bg-black text-white"
                      : "border-black/20 text-black/60 hover:border-black hover:text-black"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {list.map((a) => (
                <motion.article
                  layout
                  key={a.name}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  data-testid={`aircraft-card-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex flex-col border border-black/10 bg-white transition-shadow duration-300 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)]"
                >
                  <div className="overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.name}
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/40">{a.category}</p>
                    <h3 className="mt-2 font-display text-2xl tracking-tight">{a.name}</h3>
                    <div className="mt-5 grid grid-cols-3 gap-3 border-y border-black/10 py-4 text-[12px]">
                      <div>
                        <Users size={14} className="text-black/35" />
                        <p className="mt-1.5 text-black/45">Passengers</p>
                        <p className="font-medium text-black/80">{a.passengers}</p>
                      </div>
                      <div>
                        <Gauge size={14} className="text-black/35" />
                        <p className="mt-1.5 text-black/45">Typical range</p>
                        <p className="font-medium text-black/80">{a.range}</p>
                      </div>
                      <div>
                        <Luggage size={14} className="text-black/35" />
                        <p className="mt-1.5 text-black/45">Baggage</p>
                        <p className="font-medium text-black/80">{a.baggage}</p>
                      </div>
                    </div>
                    <p className="mt-4 flex-1 text-[13px] leading-relaxed text-black/55">{a.cabin}</p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <Link
                        to={`${PA_BASE}/charter?aircraft=${encodeURIComponent(a.name)}#request`}
                        data-testid={`request-aircraft-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="inline-flex items-center gap-2 border border-black px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-black hover:text-white"
                      >
                        Request this aircraft <ArrowUpRight size={13} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => downloadAircraftBrief(a)}
                        data-testid={`download-brief-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="inline-flex items-center gap-2 border border-black/15 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-black/55 transition-colors duration-300 hover:border-black hover:text-black"
                      >
                        Download brief
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>

          <Reveal>
            <p className="mt-12 max-w-2xl text-[12px] leading-relaxed text-black/40">
              Aircraft examples available through our global operator network. Figures are approximate
              manufacturer data; the specific aircraft offered for your trip is confirmed at quotation.
              JS Private Aviation is a charter brokerage and does not own, operate or maintain aircraft.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
